import { useEffect, useRef, useState } from 'react';

/**
 * Reveal-on-scroll hook.
 *  - Returns [ref, isInView] — attach ref to any DOM element.
 *  - First time the element enters the viewport, isInView becomes true.
 *  - Used together with the `.reveal` Tailwind utility for fade-up entrances.
 */
export function useInView({ threshold = 0.15, rootMargin = '0px 0px -10% 0px' } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      // SSR / very old browsers — show immediately
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [inView, threshold, rootMargin]);

  return [ref, inView];
}

/**
 * Reveal — drop-in wrapper that fades + slides children into view on first
 * scroll into the viewport.
 *
 * <Reveal delay={0.1}><h2>…</h2></Reveal>
 */
export function Reveal({
  children,
  as: Tag = 'div',
  delay = 0,
  duration = 0.7,
  y = 24,
  className = '',
  style = {},
  ...rest
}) {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: inView ? 1 : 0,
        transform: inView ? 'translate3d(0, 0, 0)' : `translate3d(0, ${y}px, 0)`,
        transition: `opacity ${duration}s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform ${duration}s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        willChange: 'opacity, transform',
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
